import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import type { UpdateBenchmarkBody } from '../../generated';
import { updateBenchmarkV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdPatch } from '../../generated';
import { type CustomBenchmark, toCustomBenchmark } from '../../queries/get-benchmark';

/*
 * PATCH /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}, live behind the API's `custom-benchmarks` flag.
 * Omitted fields stay as they are. Tests answer this path with `tests/mocks/benchmark-handlers.ts`.
 */

/**
 * The generated `UpdateBenchmarkBody` with `name` narrowed to a string: the spec allows `null`, but the API refuses
 * an explicit `null` name, so the type keeps callers from sending one.
 */
export type UpdateBenchmarkRequest = Omit<UpdateBenchmarkBody, 'name'> & { name?: string };

export type UpdateBenchmarkMutationVariables = { workspace_id: string; benchmark_id: string } & UpdateBenchmarkRequest;

export type UpdateBenchmarkMutation = CustomBenchmark;

async function updateBenchmark({ workspace_id, benchmark_id, ...body }: UpdateBenchmarkMutationVariables) {
  const { data } = await updateBenchmarkV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdPatch({
    path: { workspace_id, benchmark_id },
    body,
    throwOnError: true,
  });
  return toCustomBenchmark(data);
}

export const useUpdateBenchmarkMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<UpdateBenchmarkMutation, TError, UpdateBenchmarkMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<UpdateBenchmarkMutation, TError, UpdateBenchmarkMutationVariables, TContext>({
    mutationKey: ['UpdateBenchmark'],
    mutationFn: updateBenchmark,
    ...options,
  });
};

useUpdateBenchmarkMutation.fetcher = (variables: UpdateBenchmarkMutationVariables) => () => updateBenchmark(variables);
